import React, { createContext, useContext, useState, useCallback, useMemo, ReactNode } from 'react';
import { areaTranslations } from '../i18n';
import { setAppLanguage } from '../i18n/locale';

type Language = 'en' | 'de';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}

const translations: Record<Language, Record<string, string>> = {
  en: {
// ==================== APP WALKTHROUGH ====================
    walkthrough_welcome_title: 'Welcome to Asset Desk! 🎉',
    walkthrough_welcome_desc: 'Let us show you around your investment portal. This quick tour will help you navigate the key features.',
    walkthrough_dashboard_title: 'Dashboard',
    walkthrough_dashboard_desc: 'This is your home base. View your portfolio summary, latest news, and quick access to all features.',
    walkthrough_properties_title: 'Properties',
    walkthrough_properties_desc: 'View all your properties, track construction progress, manage documents, and see payment schedules.',
    walkthrough_portfolio_title: 'Portfolio Overview',
    walkthrough_portfolio_desc: 'See your total investment value, track gains, and get a complete overview of your real estate portfolio.',
    walkthrough_support_title: 'Support & AI Assistant',
    walkthrough_support_desc: 'Get help anytime! Contact your advisor, use our AI assistant, or reach out via WhatsApp.',
    walkthrough_menu_title: 'Menu & Settings',
    walkthrough_menu_desc: 'Access ROI calculator, cashflow tools, profile settings, language options, and more. You can also switch between English and German here.',
    walkthrough_complete_title: "You're All Set! ✨",
    walkthrough_complete_desc: "You now know the basics. Explore your portfolio and reach out to support if you need any help. Happy investing!",
    startTour: 'Start Tour',
    // ==================== NAVIGATION ====================
    dashboard: 'Dashboard',
    properties: 'Properties',
    portfolio: 'Portfolio',
    support: 'Support',
    menu: 'Menu',
    home: 'Home',
    profile: 'Profile',
    settings: 'Settings',
    logout: 'Logout',
    login: 'Login',
    back: 'Back',
    backToProperties: 'Back to Properties',
    // ==================== MENU ITEMS ====================
    investmentTools: 'Investment Tools',
    roiCalculator: 'ROI Calculator',
    analysis: 'Investment Analysis',
    payments: 'Payments',
    servicesAndProperties: 'Services & Properties',
    services: 'Services',
    marketplace: 'Services',
    imProperties: 'IM Properties',
    eventsAndNews: 'Events & News',
    events: 'Events',
    webinar: 'Webinars',
    news: 'Market News',
    experience: 'Experience',
    account: 'Account',
    myProfile: 'My Profile',
    cashflow: 'Cashflow',
    aiAssistant: 'AI Assistant',
    community: 'Community',
    resources: 'Resources',
    // ==================== RESOURCES ====================
    res_title: 'Resources & Documents',
    res_subtitle: 'All important documents for your property investment',
    res_searchDocuments: 'Search documents...',
    res_documentsFound: 'documents found',
    res_noDocumentsFound: 'No documents found',
    res_loadingDocument: 'Loading document...',
    res_openExternal: 'Open External',
    res_allResources: 'All Resources',
    res_financeAndBanking: 'Finance & Banking',
    res_visaAndLegal: 'Visa & Legal',
    res_purchaseProcess: 'Purchase Process',
    res_propertyManagement: 'Property Management',
    res_guides: 'Guides',
    // Resource Names
    res_mortgage: 'Mortgage Information',
    res_bankAccount: 'Bank Account Opening - ENBD vs. Mashreq',
    res_goldenVisa: 'Golden Visa',
    res_homeOwner: 'Home Owner Association',
    res_willRegistration: 'Will Registration',
    res_visaPhoto: 'Guidelines for Golden Visa Photo',
    res_snaggingOffer: 'Snagging and Hand Over Offer',
    res_snaggingReport: 'Snagging Report Sample',
    res_snaggingBrochure: 'Property Snagging Brochure',
    res_buyerProcess: 'Dubai Property Sales Process – Buyer Process',
    res_salesProgression: 'Sales Progression',
    res_dewaRegistration: 'DEWA Registration Process',
    res_propertyManagementAgreement: 'Premium Property Management Agreement',
    res_masterStrategy: 'Master Strategy Sheet – Handover, Services & Rental Process',
    res_webexGuide: 'How to Join Webex for Last Will Appointment',
    // ==================== DASHBOARD ====================
    welcomeBack: 'Welcome back',
    goodMorning: 'Good Morning',
    goodAfternoon: 'Good Afternoon',
    goodEvening: 'Good Evening',
    portfolioOverview: 'Portfolio Overview',
    heresYourPortfolio: "Here's your portfolio overview",
    totalPortfolioValue: 'Total Portfolio Value',
    portfolioValue: 'PORTFOLIO VALUE',
    totalUnits: 'Total Units',
    totalInvestment: 'Total Investment',
    currentValue: 'Current Value',
    totalGains: 'Total Gains',
    activeInvestments: 'Active Investments',
    quickActions: 'Quick Actions',
    recentActivity: 'Recent Activity',
    viewAll: 'View All',
    viewDetails: 'View Details',
    showLess: 'Show Less',
    investor: 'Investor',
    premiumMember: 'Premium Member',
    notifications: 'Notifications',
    markAllRead: 'Mark all read',
    // ==================== PORTFOLIO VIEW ====================
    portfolioSummary: 'Portfolio Summary',
    yourInvestmentOverview: 'Your investment overview',
    purchaseValueLabel: 'PURCHASE VALUE',
    currentValueLabel: 'CURRENT VALUE',
    totalGainsLabel: 'TOTAL GAINS',
    yourProperties: 'Your Properties',
    xProperties: 'properties',
    vacant: 'Vacant',
    occupied: 'Occupied',
    offplan: 'Off-plan',
    construction: 'Construction',
    toBeRentedOut: 'To Be Rented Out',
    undergoingUpgrade: 'Undergoing Upgrade',
    // ==================== PROPERTIES ====================
    myProperties: 'My Properties',
    propertyDetails: 'Property Details',
    unitNumber: 'Unit Number',
    unit: 'Unit',
    project: 'Project',
    projectName: 'Project Name',
    developer: 'Developer',
    location: 'Location',
    area: 'Area',
    sqft: 'sqft',
    bedrooms: 'Bedrooms',
    bathrooms: 'Bathrooms',
    propertyType: 'Property Type',
    purchasePrice: 'Purchase Price',
    purchaseValue: 'Purchase Value',
    purchaseDate: 'Purchase Date',
    completionDate: 'Completion Date',
    noPropertiesYet: 'No Properties Yet',
    noPropertiesDesc: 'Your properties will appear here once added.',
    noPropertiesFound: 'No Properties Found',
    addPropertiesToCalculate: 'Add properties to your portfolio to calculate ROI.',
    addProperty: 'Add Property',
    createNewProperty: 'Create a new property folder',
    createProperty: 'Create Property',
    creating: 'Creating...',
    propertyName: 'Property Name',
    enterPropertyName: 'Enter property name',
    pleaseEnterPropertyName: 'Please enter a property name',
    errorCreatingProperty: 'Error creating property. Please try again.',
    browseProperties: 'Browse Properties',
    exploreProperties: 'Explore Properties',
    apartment: 'Apartment',
    villa: 'Villa',
    townhouse: 'Townhouse',
    penthouse: 'Penthouse',
    studio: 'Studio',
    bedroom: 'Bedroom',
    floor: 'Floor',
    projectStatus: 'Project Status',
    projectProgress: 'Project Progress',
    completed: 'Completed',
    inProgress: 'In Progress',
    priceAndValuation: 'Price & Valuation',
    gains: 'Gains (%)',
    summary: 'Summary',
    income: 'Income',
    incomeDetails: 'Income Details',
    monthlyRental: 'Monthly Rental',
    annualIncome: 'Annual Income',
    searchPlaceholder: 'Search by unit, filename, etc...',
    requestDocuments: 'Request Documents',
    showingAll: 'Showing all',
    searchProperties: 'Search properties...',
searchUnits: 'Search units...',
requestAddUnit: "Can't find your unit? Request to add",
unitRequestSent: 'Request sent! Our team will add the unit shortly.',
tapToUpload: 'Tap to upload',
selectUnitFirst: 'Select a unit first',
aiExtraction: 'AI Extraction',
aiExtractionDesc: 'Property data will be automatically extracted from this document',
processingAI: 'Processing with AI - Extracting property data...',
uploadingDoc: 'Uploading document...',
iHaveDocument: 'I have a document',
enterManually: 'Enter details manually',
projectPlaceholder: 'e.g. Aeon Tower, Ocean Star, Clearpoint',
unitPlaceholder: 'e.g. Building 1, Unit 905 or Villa 23',
docUploadNote: 'We will create the unit and contact you to upload the document.',
enterProjectUnit: 'Please enter project name and unit number',
    // ==================== DOCUMENTS ====================
    documents: 'Documents',
    uploadDocument: 'Upload Document',
    noDocuments: 'No documents yet',
    noDocumentsUploaded: 'No documents uploaded yet',
    viewDocument: 'View Document',
    addDocuments: 'Add Documents',
    uploadToProperty: 'Upload documents to your property',
    emptyFolderNote: 'Please upload a document to update the data of your property',
    emptyFolderUploadBtn: 'Upload Document Now',
    verificationPending: 'Pending Verification',
    verificationVerified: 'Verified',
    verificationRejected: 'Data Rejected — Please re-upload',
    verificationInProgress: 'Our team is reviewing the AI-extracted data',
    verificationComplete: 'Document data has been verified and applied',
    verificationRejectedNote: 'The extracted data could not be verified. Please upload a clearer document.',
    dataExtractedByAI: 'Data extracted by AI',
    awaitingAdminReview: 'Awaiting admin review before updating property data',
    documentCategory: 'Document Category',
    unitDocuments: 'Unit Documents',
    personalDocuments: 'Personal Documents',
    selectUnit: 'Select Unit',
    selectAUnit: 'Select a unit...',
    documentType: 'Document Type',
    chooseFile: 'Choose File',
    uploading: 'Uploading...',
    passport: 'Passport',
    emiratesId: 'Emirates ID',
    visa: 'Visa',
    paymentProof: 'Payment Proof',
    floorPlan: 'Floor Plan',
    showing: 'Showing',
    all: 'All',
    // ==================== PAYMENT SCHEDULE / CASHFLOW ====================
    paymentSchedule: 'Payment Schedule',
    installmentPlan: 'Installment Plan',
    trackPaymentMilestones: 'Track your investment payment milestones',
    noPaymentSchedule: 'No payment schedule found.',
    uploadSpaForPayments: 'Upload an SPA document to see payment details',
    upcomingPayments: 'Upcoming Payments',
    paymentAmount: 'Payment Amount',
    dueDate: 'Due Date',
    paymentProgress: 'Payment Progress',
    allProperties: 'All Properties',
    paidLabel: 'Paid',
    upcomingLabel: 'Upcoming',
    tpChequeSubmitted: 'With the owner',
    overdueLabel: 'Overdue',
    remainingLabel: 'Remaining',
    perUnit: 'Per Unit',
    yearlySummary: 'Yearly Summary',
    yearlyPayments: 'Yearly Payments',
    noInstallmentData: 'No installment data available',
    uploadSpaOtp: 'Upload SPA, OTP, or SOA to see your payment schedule',
    extractFromDocs: 'Extract from existing documents',
    monthlyTotal: 'Monthly Total',
    // ==================== CASHFLOW COMING SOON ====================
    cashflowComingSoon: 'Cashflow Tracker',
    comingSoon: 'Coming Soon',
    cashflowComingSoonDescription: 'We\'re working on an advanced cashflow tracking system to help you monitor your payment schedules, track milestones, and manage your property investments more effectively.',
    cashflowFeature: 'Cashflow Tracking',
    cashflowComingSoonDesc: 'We\'re building a powerful cashflow management system to help you track payments, milestones, and investment progress.',
    upcomingFeatures: 'What\'s Coming',
    paymentTracking: 'Payment Tracking',
    paymentTrackingDesc: 'Track all your property payments with status updates and due date reminders.',
    milestoneAlerts: 'Milestone Alerts',
    milestoneAlertsDesc: 'Get notified about upcoming payment milestones and construction progress.',
    progressReports: 'Progress Reports',
    progressReportsDesc: 'Visual charts and reports showing your investment cashflow over time.',
    stayTuned: 'Stay tuned! This feature will be available soon.',
    // ==================== INCOME COMING SOON ====================
    incomeTracking: 'Income Tracking',
    incomeComingSoonDesc: 'We\'re building comprehensive income tracking to help you monitor rental income, ROI, and financial performance.',
    roiAnalysis: 'ROI Analysis',
    expenseTracking: 'Expense Tracking',
    featureInDevelopment: 'Feature in development',
    // ==================== PROFILE ====================
    profileInformation: 'Profile Information',
    managePersonalInfo: 'Manage your personal information',
    editProfile: 'Edit Profile',
    fullName: 'Full Name',
    emailAddress: 'Email Address',
    phoneNumber: 'Phone Number',
    nationality: 'Nationality',
    dateOfBirth: 'Date of Birth',
    accountId: 'Account ID',
    changePassword: 'Change Password',
    updateAccountPassword: 'Update your account password',
    currentPassword: 'Current Password',
    newPassword: 'New Password',
    confirmPassword: 'Confirm Password',
    enterCurrentPassword: 'Enter current password',
    enterNewPassword: 'Enter new password',
    // ==================== ROI CALCULATOR ====================
    roiCalculatorTitle: 'Investment ROI Calculator',
    investmentReturnAnalysis: 'Investment Return Analysis',
    investmentAnalysis: 'Investment Analysis',
    appreciation: 'Appreciation',
    units: 'Units',
    allUnits: 'All Units',
    paidPercent: 'Paid %',
    fullyPaid: 'Fully Paid',
    sellingPrice: 'Selling Price',
    paidInstallment: 'Paid Installment',
    grossNet: 'Gross Net',
    profit: 'Profit',
    unitBreakdown: 'Unit Breakdown',
    originalPrice: 'Original Price',
    originalValue: 'Original Value',
    paid: 'Paid',
    paidAmount: 'Amount Paid',
    amountPaid: 'Amount Paid',
    netToSeller: 'Net to Seller',
    dldFee: 'DLD Fee',
    brokerFee: 'Broker Fee',
    brokerCommission: 'Broker Commission',
    totalOriginal: 'Total Original',
    totalSelling: 'Total Selling',
    totalPaid: 'Total Paid',
    totalFees: 'Total Fees',
    totalGrossNet: 'Total Gross Net',
    remainingBalance: 'Remaining Balance',
    totalProfit: 'Total Profit',
    profitDividedByInvestment: 'Profit ÷ Total Investment',
    profitDividedByEquity: 'Profit ÷ Equity',
    howRoiCalculated: 'How ROI & ROE are Calculated',
    understandingRoiCalculator: 'Understanding ROI Calculator',
    whatIsRoi: 'What is ROI (Return on Investment)?',
    whatIsRoe: 'What is ROE (Return on Equity)?',
    whyRoeHigher: 'Why is ROE higher than ROI?',
    whatFeesIncluded: 'What fees are included?',
    howProfitCalculated: 'How is Profit calculated?',
    roiExplanation: 'ROI measures profit relative to the total property cost. It shows your gain as a percentage of the full investment value.',
    roeExplanation: 'ROE measures profit relative to your actual cash invested (equity). For off-plan, this is the amount you have paid so far.',
    roeHigherExplanation: 'ROE is higher because you are measuring returns against only the money you have invested, not the full property value. This is the power of leverage in off-plan properties.',
    // ==================== ROI CALCULATOR - CORPORATE STRUCTURING ====================
    corporateStructuring: 'Corporate Structuring',
    structuringRecommendation: 'Structuring Recommendation',
    yourProjectedProfit: 'Your Projected Profit',
    structuringNotNeeded: 'Structuring not necessary',
    structuringNotNeededDesc: 'At this profit level, the costs of setting up a corporate structure typically outweigh the benefits.',
    considerStructuring: 'Consider Structuring',
    considerStructuringDesc: 'Your profit level is entering the range where a corporate structure could provide tax optimization benefits.',
    structuringRecommended: 'Structuring Recommended',
    structuringRecommendedDesc: 'At this profit level, a proper corporate structure can significantly optimize your tax situation.',
    structuringHighlyRecommended: 'Structuring Highly Recommended',
    structuringHighlyRecommendedDesc: 'With profits at this level, professional tax structuring is essential for optimal wealth preservation.',
    structureMakesSenseFrom: 'A corporate structure typically makes sense from',
    profitOnwards: 'profit onwards',
    basedOnYourPortfolio: 'Based on your portfolio analysis',
    bookTaxConsultation: 'Book Tax Consultation',
    consultationDesc: 'Discuss corporate structuring, tax optimization & international planning',
    freeConsultation: 'Free Initial Consultation',
    withTaxExpert: 'with our international tax experts',
    potentialSavings: 'Potential Tax Savings',
    savingsDisclaimer: 'Estimated savings depend on individual circumstances and home country tax rates.',
    thresholdReached: 'Threshold Reached',
    belowThreshold: 'Below Threshold',
    // ==================== CORPORATE SERVICES UAE ====================
    corporateServicesUae: 'Corporate Services UAE',
    companyManagement: 'Company Management Dashboard',
    companyFormation: 'Company Formation Emirates',
    documentHub: 'Document Hub',
    tradeLicense: 'Trade License',
    moa: 'Memorandum of Association (MOA)',
    unitLeaseAgreement: 'Unit Lease Agreement',
    establishmentCard: 'Establishment Card',
    bankAccountOpening: 'Bank Account Opening',
    visaSetup: 'Visa Setup',
    deadlinesCompliance: 'Deadlines & Compliance',
    ctAndFilings: 'Corporate Tax & Filings',
    accounting: 'Accounting',
    receiptsAndExpenses: 'Receipts & Expenses',
    licenseManagement: 'License Management',
    renewalAndCompliance: 'Renewal & Compliance',
    vatManagement: 'VAT Management',
    valueAddedTax: 'Value Added Tax',
    licenseNo: 'License No',
    formationProgress: 'Formation Progress',
    licenseExpiry: 'License Expiry',
    vatStatus: 'VAT Status',
    ctStatus: 'CT Status',
    days: 'days',
    daysLeft: 'days left',
    daysRemaining: 'days remaining',
    registered: 'Registered',
    notRequired: 'Not Required',
    actionRequired: 'Action Required',
    compliant: 'Compliant',
    actionNeeded: 'Action Needed',
    required: 'Required',
    optional: 'Optional',
    verified: 'Verified',
    pending: 'Pending',
    upload: 'Upload',
    overallProgress: 'Overall Progress',
    ctRegistrationRequired: 'CT Registration Required',
    ctRegistrationDeadline: 'Registration Deadline',
    registerNow: 'Register Now',
    ctFilingDue: 'CT Filing Due',
    nextFilingDeadline: 'Next Filing Deadline',
    bookAppointment: 'Book Appointment',
    submitFiling: 'Submit Filing',
    uploadReceipt: 'Upload Receipt',
    receipts: 'receipts',
    licenseExpiryWarning: 'License Expiring Soon!',
    licenseExpiresOn: 'License expires on',
    renewLicense: 'Renew License',
    licenseValid: 'License Valid',
    expiresOn: 'Expires on',
    pushNotificationInfo: 'Push Notification Enabled',
    licenseReminderNote: 'You will receive a reminder 2 months before your license expires.',
    revenueProgress: 'Revenue vs VAT Threshold',
    vatThresholdNote: 'VAT registration is mandatory when revenue exceeds AED 375,000',
    vatRegistrationRequired: 'VAT Registration Required',
    vatThresholdExceeded: 'Your revenue has exceeded the VAT threshold. Registration is mandatory.',
    registerForVat: 'Register for VAT Now',
    vatNotRequired: 'VAT Not Required',
    vatBelowThreshold: 'Your revenue is below the VAT threshold',
    remaining: 'remaining',
    registrationRequired: 'Registration Required',
    vatDocuments: 'VAT Documents',
    uploadVatReceipt: 'Upload VAT Receipt',
    uploadVatFiling: 'Upload VAT Filing',
    needHelp: 'Need help with your company?',
    clickToUpload: 'Click or drag to upload',
    // NEW: Corporate Services - Company Setup & Management
    setupYourCompany: 'Set up your UAE company',
    companySetup: 'Company Setup',
    companyName: 'Company Name',
    licenseNumber: 'License Number',
    freeZone: 'Free Zone',
    createCompany: 'Create Company',
    companySetupNote: 'You can update these details later in settings.',
    expired: 'Expired',
    checkWithAdvisor: 'Check with advisor',
    toBeConfirmed: 'To be confirmed',
    confirmDeleteDocument: 'Are you sure you want to delete this document?',
    confirmDeleteReceipt: 'Are you sure you want to delete this receipt?',
    pleaseEnterCompanyDetails: 'Please enter company name and license number',
    pleaseEnterReceiptDetails: 'Please enter description and amount',
    description: 'Description',
    date: 'Date',
    addReceipt: 'Add Receipt',
    noReceiptsYet: 'No receipts uploaded yet',
    viewReceipts: 'View Receipts',
    totalVatPaid: 'Total VAT Paid',
    amountIncludesVat: 'Amount includes VAT (5%)',
    // VAT Self-Calculator
    vatSelfCalculator: 'VAT Self-Calculator',
    currentRevenue: 'Current Revenue',
    vatThreshold: 'VAT Threshold',
    amountOverThreshold: 'Amount Over Threshold',
    estimatedVatOwed: 'Estimated VAT Owed',
    vatCalculatorNote: 'This is an estimate. Consult with a tax advisor for accurate calculations.',
    // Formation Timeline
    nextStep: 'Next Step',
    step: 'Step',
    current: 'Current',
    uploadNow: 'Upload Now',
    // Apple Pay
    applePayComingSoon: 'Apple Pay integration coming soon! For now, please book an appointment to arrange payment.',
    // License Expired
    licenseExpired: 'License Expired!',
    // ==================== CASHFLOW CALCULATOR ====================
    cashflowCalculator: 'Cashflow Calculator',
    grossNetAnalysis: 'Gross-Net Analysis for Property Investment',
    property: 'Property',
    year: 'Year',
    totalIncome: 'Total Income',
    totalExpenses: 'Total Expenses',
    netCashflow: 'Net Cashflow',
    totalRoi: 'Total ROI',
    annual: 'Annual',
    includingAppreciation: 'Including appreciation',
    propertyAppreciation: 'Property Appreciation',
    expenses: 'Expenses',
    addExpense: 'Add Expense',
    addIncome: 'Add Income',
    category: 'Category',
    amount: 'Amount',
    enterAmount: 'Enter amount',
    frequency: 'Frequency',
    oneTime: 'One-time',
    monthly: 'Monthly',
    quarterly: 'Quarterly',
    incomeType: 'Income Type',
    pleaseSelectProperty: 'Please select a property first',
    pleaseEnterAmount: 'Please enter an amount',
    notApplied: 'Not Applied',
    serviceCharges: 'Service Charges',
    dewa: 'DEWA (Electricity/Water)',
    propertyManagement: 'Property Management',
    maintenance: 'Maintenance & Repairs',
    insurance: 'Insurance',
    mortgagePayment: 'Mortgage Payment',
    furnishing: 'Furnishing',
    agencyFees: 'Agency Fees',
    legalFees: 'Legal Fees',
    other: 'Other',
    rentalIncome: 'Rental Income',
    airbnbIncome: 'Airbnb Income',
    expectedAppreciation: 'Expected Appreciation',
    otherIncome: 'Other Income',
    // ==================== SUPPORT ====================
    supportTitle: 'Support',
    howCanWeHelp: 'How can we help?',
    yourAccountManager: 'Your Account Manager',
    yourPersonalAdvisor: 'Your Personal Advisor',
    seniorInvestmentAdvisor: 'Senior Investment Advisor',
    noAssignedAdvisor: 'No Assigned Advisor',
    noAssignedSupport: 'No Assigned Support',
    messageOnWhatsApp: 'Message on WhatsApp',
    connecting: 'Connecting...',
    callUs: 'Call Us',
    email: 'Email',
    emailUs: 'Email Us',
    whatsapp: 'WhatsApp',
    liveChat: 'Live Chat',
    otherWaysToReach: 'Other ways to reach us',
    getInstantAnswers: 'Get instant answers to your questions',
    needToTalkHuman: 'Need to talk to a human?',
    aiAssistantTitle: 'Asset Desk AI Assistant',
    welcomeToAI: 'Welcome to Asset Desk AI',
    aiWelcomeMessage: "I'm your personal investment assistant. Ask me anything about Dubai real estate, your portfolio, or investment strategies.",
    typeMessage: 'Type your message...',
    // ==================== SECURITY ====================
    twoFaDisabledDesc: '⚠ 2FA is disabled. Enable it to add an extra layer of security to your account.',
    twoFaEnabledDesc: '✓ 2FA is currently enabled. Your account has an extra layer of security.',
    enableTwoFactorAuth: 'Enable Two-Factor Authentication',
    faceRecognitionDisabledDesc: '⚠ Face recognition is disabled. Enable it for quick and secure login.',
    faceRecognitionEnabledDesc: '✓ Face recognition is enabled. You can login using your face.',
    enableFaceRecognition: 'Enable Face Recognition',
    disabled: 'DISABLED',
    enabled: 'ENABLED',
    settingUp: 'Setting up...',
    // ==================== MAIN DASHBOARD ====================
    investInDubai: 'Invest in Dubai Real Estate',
    discoverExclusiveProperties: 'Discover exclusive off-plan properties with guaranteed returns',
    dubaiRealEstateNews: 'Dubai Real Estate News',
    latestNewsSubtitle: "Latest news and updates from Dubai's property market",
    documentRequest: 'Document Request',
    serviceRequest: 'Service Request',
    pointOfContact: 'Point of Contact',
    bookConsultant: 'Book Consultant',
    requests: 'Requests',
    paymentDue: 'Payment Due',
    nextPaymentIn7Days: 'Next payment in 7 days',
    constructionUpdate: 'Construction Update',
    newDocument: 'New Document',
    uploaded: 'uploaded',
    newProperties: 'New Properties',
    checkLatestOfferings: 'Check our latest offerings',
    viewBrochure: 'View Brochure',
    watchVideo: 'Watch Video',
    upcomingProjects: 'Upcoming Projects',
    exclusiveLaunches: 'Exclusive launches coming soon',
    // ==================== PROPERTY VIEW ====================
    paymentDetails: 'Payment Details',
    milestone: 'Milestone',
    status: 'Status',
    valuation: 'Valuation',
    overduePaymentsAlert: 'Overdue Payments Alert',
    uploadDocsNote: 'Upload documents to update your property data',
personalDocs: 'Personal',
uploadPersonalDoc: 'Upload Personal Document',
noPersonalDocs: 'No personal documents yet',
uploadPassportVisa: 'Upload your Passport, Visa, or Emirates ID',
passportNo: 'Passport No',
visaNo: 'Visa No',
visaType: 'Visa Type',
idNo: 'ID No',
expiry: 'Expiry',
companyDocs: 'Company',
uploadCompanyDoc: 'Upload Company Document',
noCompanyDocs: 'No company documents yet',
uploadCompanyDocsDesc: 'Upload your Trade License, MOA, Power of Attorney, and more',
companyDocuments: 'Company Documents',
    // ==================== SECURITY SETTINGS ====================
    securitySettings: 'Security Settings',
    twoFactorAuth: 'Two-Factor Authentication (2FA)',
    facialRecognitionLogin: 'Facial Recognition Login',
    faceRecognition: 'Face Recognition',
    enableFacialRecognition: 'Enable Facial Recognition',
    disableFaceRecognition: 'Disable Face Recognition',
    viewBackupCodes: 'View Backup Codes',
    scanQrCode: 'Scan this QR code with your authenticator app (Google Authenticator, Authy, etc.)',
    cantScanEnterManually: "Can't scan? Enter this code manually:",
    step2SaveBackupCodes: 'Step 2: Save Backup Codes',
    copyBackupCodes: 'Copy Backup Codes',
    step3VerifySetup: 'Step 3: Verify Setup',
    enterCodeFromAuthenticator: 'Enter code from your authenticator app',
    saveBackupCodesWarning: 'Save these backup codes in a safe place. You can use them to access your account if you lose your phone.',
    copyCodes: 'Copy Codes',
    positionFaceInstruction: "Position your face in the center of the camera. Make sure you're in a well-lit area.",
    faceDataEncrypted: 'Your face data is encrypted and stored securely',
    // ==================== BIOMETRIC AUTH ====================
    biometricLogin: 'Biometric Login',
    enableBiometricLogin: 'Enable Biometric Login',
    disableBiometricLogin: 'Disable Biometric Login',
    biometricLoginEnabled: 'Biometric login enabled successfully!',
    biometricLoginDisabled: 'Biometric login has been disabled.',
    biometricNotAvailable: 'Biometric authentication is not available on this device',
    biometricNotSetUp: 'Face ID is not set up yet. Sign in with your password once and the app will offer it.',
    faceIdOffer: 'Use Face ID to sign in next time?\n\nNext time you open the app, just look at your phone. You can turn it off any time in your profile.',
    enterPasswordToEnable: 'Enter your password to enable biometric login:',
    incorrectPassword: 'Incorrect password. Please try again.',
    authenticationCancelled: 'Authentication was cancelled',
    authenticationFailed: 'Biometric authentication failed',
    passwordMayHaveChanged: 'Login failed. Your password may have changed. Please login with password.',
    loginWithBiometric: 'Login with Face ID / Fingerprint',
    useFaceOrFingerprint: 'Use your face or fingerprint to login',
    accountNotFound: 'Account not found',
    // ==================== MARKETPLACE/PROPERTIES ====================
    availableProperties: 'Available Properties',
    browseCompletePortfolio: 'Browse our complete portfolio of Dubai properties available for investment',
    bookAnAppointment: 'Book an Appointment',
    joinMeeting: 'Join Meeting',
    requestMeetingLink: 'Request Meeting Link',
    tapToInquireWhatsApp: 'Tap to inquire via WhatsApp',
    tapToInquireChat: 'Tap to inquire via Chat',
    // ==================== MENU/PROFILE SECTION ====================
    totalProperties: 'Total Properties',
    accountAge: 'Account Age',
    yourInvestmentTeam: 'Your Investment Team',
    dedicatedContacts: 'Your dedicated contacts at Asset Desk',
    investmentAdvisor: 'Investment Advisor',
    supportContact: 'Support Contact',
    totalCapitalGain: 'Total Capital Gain',
    capitalGain: 'Capital Gain',
    trackInstallments: 'Track your installment payments',
    followStepsToSchedule: 'Follow the steps below to schedule your appointment.',
    selectServiceType: '1. Select Service Type',
    corporateServices: 'Corporate Services',
    conveyanceServices: 'Conveyance Services',
    selectService: '2. Select a Service',
    chooseService: '-- Choose a service --',
    selectAvailableTime: '4. Select an Available Time',
    // ==================== COMMON ====================
    loading: 'Loading...',
    loadingNews: 'Loading latest news...',
    save: 'Save',
    cancel: 'Cancel',
    confirm: 'Confirm',
    delete: 'Delete',
    edit: 'Edit',
    close: 'Close',
    submit: 'Submit',
    search: 'Search',
    filter: 'Filter',
    error: 'Error',
    success: 'Success',
    aed: 'AED',
    bookConsultation: 'Book a Consultation',
    yourBookedAppointments: 'Your Booked Appointments',
    footerText: '© 2026 Asset Desk Real Estate LLC. Premium Investment Portfolio Management.',
    smartUpload: 'Smart Upload',
    smartUploadDesc: 'Upload all your files at once - AI will sort everything automatically',
    smartUploadBtn: 'Upload All Documents (AI Smart Upload)',
    smartUploadHint: 'Select all your files at once - AI sorts & verifies automatically',
    tapToUploadAll: 'Tap here to upload all documents',
    smartUploadFormats: 'Select multiple files - PDF, JPG, PNG (Max 100MB each)',
    smartUploadVerify: 'All documents are verified by our team before appearing in your account',
    orUploadManually: 'OR UPLOAD MANUALLY',
    emailDocuments: 'Email Documents',
    emailDocumentsDesc: 'Send all your documents to our email - we handle the rest',
    emailDocumentsBtn: 'Send Documents via Email',
    // ==================== VALUATION REPORT ====================
    vrTitle: 'Valuation Report',
    vrGenerated: 'Generated',
    vrDldSource: 'Dubai Land Department data',
    vrAllProperties: 'All properties',
    vrDownloadPdf: 'Download PDF report',
    vrPreparing: 'Preparing report…',
    vrProperty: 'Property',
    vrUnitNumber: 'Unit number',
    vrType: 'Type',
    vrBedrooms: 'Bedrooms',
    vrBuiltUpArea: 'Built-up area',
    vrStatus: 'Status',
    vrPurchased: 'Purchased',
    vrPurchasePrice: 'Purchase price',
    vrHandover: 'Handover',
    vrDisclaimer: 'Disclaimer',
    vrDldMarketValue: 'DLD market value',
    vrEstimateRange: 'Estimate range',
    vrPricePerSqft: 'Price per sqft',
    vrComparablesUsed: 'Comparables used',
    vrLast6Months: 'Last 6 months change',
    vrIndicativeOnly: 'Indicative only',
    vrNotEnoughComps: 'Not enough comparable sales to value this unit yet',
    vrRangeOnly: 'Range only.',
    vrHowCalculated: 'How this value is calculated',
    vrHowStep1a: 'We take the',
    vrHowStep1b: 'DLD-registered sales of comparable units listed below — real transactions, not asking prices.',
    vrHowStep2a: 'Each sale is converted to a price per sqft. We drop the cheapest tenth and the dearest tenth, then average what is left — the',
    vrHowStep2b: 'trimmed average',
    vrHowStep2c: 'is',
    vrHowStep2d: 'per sqft. Trimming removes the bargain sale and the premium penthouse, so neither can drag the figure, while the rest of the building still counts. A plain middle-sale median would ignore how far above it the better units trade.',
    vrHowStep3a: 'per sqft × this unit’s',
    vrHowStep3b: 'sqft',
    vrRangeExplain: 'The range spans the middle half of those sales — the 25th to 75th percentile.',
    vrCompsForEstimate: 'Comparables used for this estimate',
    vrCompsForEstimateSub: 'The exact DLD-registered sales behind the value above — median AED/sqft of these transactions × this unit’s size. Nothing else feeds the number.',
    vrCompsAreaScope: 'No registered sale in this building matched, so these are sales of comparable units in the surrounding area — different buildings, not this one.',
    vrCompsShowAll: 'Show all comparables',
    vrCompsShowFewer: 'Show fewer',
    vrOtherSales: 'Other recent sales in this building',
    vrOtherSalesSub: 'All unit types and sizes — shown for context only; these do NOT feed the estimate above.',
    vrColDate: 'Date',
    vrColProperty: 'Property',
    vrColBeds: 'Beds',
    vrColArea: 'Area',
    vrColSoldAt: 'Sold at',
    vrHighlySimilar: 'HIGHLY SIMILAR',
    vrOffPlan: 'off-plan',
    vrVsYours: 'vs yours',
    vrDldRef: 'DLD ref',
    vrNoComps: 'No comparable DLD sales matched this unit yet.',
    vrTwoTiersTitle: 'This building sells at two price levels.',
    vrTwoTiersOf: 'of the',
    vrTwoTiersClosedAt: 'comparable sales closed at',
    vrTwoTiersForSize: 'for this size',
    vrTwoTiersWhile: 'while',
    vrTwoTiersHigher: 'closed markedly higher at',
    vrTwoTiersNoFloor: 'typically premium floors, views or finishing. DLD records don’t state floor or view, so we cannot tell which level this unit belongs to. The estimate above is the median across all',
    vrTwoTiersEnd: 'sales; a premium unit would sit toward the higher range.',
    vrCostBreakdown: 'Cost breakdown',
    vrCostBreakdownSub: 'Total cost to acquire this property at the estimated value.',
    vrTotalCostToBuy: 'Total cost to buy',
    vrEstimatedValue: 'Estimated value',
    vrDldFee: 'DLD transfer fee',
    vrTrustee: 'Registration trustee',
    vrAgencyFee: 'Agency fee',
    vrAgainstPurchase: 'Against your purchase',
    vrUnrealisedGain: 'Unrealised gain',
    vrTrendCaption: 'Monthly median transacted price for comparable units in this building',
    vrCurrentEstimate: 'current estimate',
    vrConfidenceHigh: 'High confidence',
    vrConfidenceMedium: 'Medium confidence',
    vrConfidenceLow: 'Low confidence',
    vrConfidenceNone: 'Insufficient data',
    vrAsAt: 'as at',
    vrQualifyingComp: 'qualifying comparable',
    vrQualifyingComps: 'qualifying comparables',
    vrFewerThan3: '— fewer than 3, so a single figure would overstate what the evidence supports. The transactions below are that evidence.',
    languageLabel: 'Language',
    languageEnglish: 'English',
    languageGerman: 'Deutsch',
    vrMethodology: 'How this was calculated',
    vrMethodologyBody1: 'Median price per square foot of comparable Dubai Land Department sales, applied to this unit’s size — the industry-standard approach. Comparables are the same building and bedroom count, within ±15% of the unit’s size, over the last',
    vrMethodologyBody2: 'months, genuine resales only, with statistical outliers removed.',
    vrCompsUsedShort: 'Comps used',
    vrCompsExcluded: 'Comps excluded',
    vrMatchLevel: 'Match level',
    vrAreaMatched: 'Area matched',
    vrSource: 'Source',
    vrSynced: 'synced',
    vrTenancyTitle: 'Recent tenancy contracts',
    vrTenancySub: 'Registered Ejari leases for comparable units.',
    vrSpreadTitle: 'Spread between sources —',
    vrSpreadBody: 'Providers weight transactions and listings differently; treat the range as the answer, not one number.',
    vrColStart: 'Start',
    vrColRent: 'Annual rent',
    igSkipTutorial: 'Skip tutorial',
    igTapHere: 'Tap here',
    pnhEnable: 'Enable push notifications',
    pnhEnableSub: 'Get instant updates on your phone',
    pnhEnableBtn: 'Enable',
  },
  de: {
// ==================== APP WALKTHROUGH ====================
    walkthrough_welcome_title: 'Willkommen bei Asset Desk! 🎉',
    walkthrough_welcome_desc: 'Lassen Sie uns Ihnen Ihr Investment-Portal zeigen. Diese kurze Tour hilft Ihnen, die wichtigsten Funktionen kennenzulernen.',
    walkthrough_dashboard_title: 'Übersicht',
    walkthrough_dashboard_desc: 'Dies ist Ihre Zentrale. Sehen Sie Ihre Portfolio-Zusammenfassung, aktuelle Nachrichten und schnellen Zugriff auf alle Funktionen.',
    walkthrough_properties_title: 'Immobilien',
    walkthrough_properties_desc: 'Sehen Sie alle Ihre Immobilien, verfolgen Sie den Baufortschritt, verwalten Sie Dokumente und sehen Sie Zahlungspläne.',
    walkthrough_portfolio_title: 'Portfolio-Übersicht',
    walkthrough_portfolio_desc: 'Sehen Sie Ihren gesamten Investitionswert, verfolgen Sie Gewinne und erhalten Sie einen vollständigen Überblick über Ihr Immobilien-Portfolio.',
    walkthrough_support_title: 'Support & KI-Assistent',
    walkthrough_support_desc: 'Erhalten Sie jederzeit Hilfe! Kontaktieren Sie Ihren Berater, nutzen Sie unseren KI-Assistenten oder erreichen Sie uns per WhatsApp.',
    walkthrough_menu_title: 'Menü & Einstellungen',
    walkthrough_menu_desc: 'Zugriff auf ROI-Rechner, Cashflow-Tools, Profileinstellungen, Sprachoptionen und mehr. Hier können Sie auch zwischen Deutsch und Englisch wechseln.',
    walkthrough_complete_title: 'Sie sind startklar! ✨',
    walkthrough_complete_desc: 'Sie kennen nun die Grundlagen. Erkunden Sie Ihr Portfolio und wenden Sie sich bei Fragen an unseren Support. Viel Erfolg beim Investieren!',
    startTour: 'Tour starten',
    // ==================== NAVIGATION ====================
    dashboard: 'Übersicht',
    properties: 'Immobilien',
    portfolio: 'Portfolio',
    support: 'Support',
    menu: 'Menü',
    home: 'Start',
    profile: 'Profil',
    settings: 'Einstellungen',
    logout: 'Abmelden',
    login: 'Anmelden',
    back: 'Zurück',
    backToProperties: 'Zurück zu Immobilien',
    // ==================== MENU ITEMS ====================
    investmentTools: 'Investment-Werkzeuge',
    roiCalculator: 'ROI Rechner',
    analysis: 'Investment Analyse',
    payments: 'Zahlungen',
    servicesAndProperties: 'Services & Immobilien',
    services: 'Dienstleistungen',
    marketplace: 'Dienstleistungen',
    imProperties: 'IM Immobilien',
    eventsAndNews: 'Events & Nachrichten',
    events: 'Veranstaltungen',
    webinar: 'Webinare',
    news: 'Marktnachrichten',
    experience: 'Erfahrung',
    account: 'Konto',
    myProfile: 'Mein Profil',
    cashflow: 'Cashflow',
    aiAssistant: 'KI Assistent',
    community: 'Gemeinschaft',
    resources: 'Ressourcen',
    // ==================== RESOURCES ====================
    res_title: 'Ressourcen & Dokumente',
    res_subtitle: 'Alle wichtigen Dokumente für Ihre Immobilieninvestition',
    res_searchDocuments: 'Dokumente suchen...',
    res_documentsFound: 'Dokumente gefunden',
    res_noDocumentsFound: 'Keine Dokumente gefunden',
    res_loadingDocument: 'Dokument wird geladen...',
    res_openExternal: 'Extern öffnen',
    res_allResources: 'Alle Ressourcen',
    res_financeAndBanking: 'Finanzen & Banking',
    res_visaAndLegal: 'Visa & Rechtliches',
    res_purchaseProcess: 'Kaufprozess',
    res_propertyManagement: 'Immobilienverwaltung',
    res_guides: 'Anleitungen',
    // Resource Names
    res_mortgage: 'Hypothekeninformationen',
    res_bankAccount: 'Bankkontoeröffnung - ENBD vs. Mashreq',
    res_goldenVisa: 'Golden Visa',
    res_homeOwner: 'Wohnungseigentümergemeinschaft',
    res_willRegistration: 'Testamentsregistrierung',
    res_visaPhoto: 'Richtlinien für Golden Visa Foto',
    res_snaggingOffer: 'Mängelprüfung und Übergabeangebot',
    res_snaggingReport: 'Mängelprotokoll Beispiel',
    res_snaggingBrochure: 'Immobilien-Mängelprüfung Broschüre',
    res_buyerProcess: 'Dubai Immobilienverkaufsprozess – Käuferprozess',
    res_salesProgression: 'Verkaufsfortschritt',
    res_dewaRegistration: 'DEWA Registrierungsprozess',
    res_propertyManagementAgreement: 'Premium Immobilienverwaltungsvertrag',
    res_masterStrategy: 'Master-Strategieblatt – Übergabe, Services & Vermietungsprozess',
    res_webexGuide: 'Wie man Webex für den Testamentstermin beitritt',
    // ==================== DASHBOARD ====================
    welcomeBack: 'Willkommen zurück',
    goodMorning: 'Guten Morgen',
    goodAfternoon: 'Guten Tag',
    goodEvening: 'Guten Abend',
    portfolioOverview: 'Portfolio Übersicht',
    heresYourPortfolio: 'Hier ist Ihre Portfolio-Übersicht',
    totalPortfolioValue: 'Gesamtportfoliowert',
    portfolioValue: 'PORTFOLIOWERT',
    totalUnits: 'Gesamteinheiten',
    totalInvestment: 'Gesamtinvestition',
    currentValue: 'Aktueller Wert',
    totalGains: 'Gesamtgewinn',
    activeInvestments: 'Aktive Investments',
    quickActions: 'Schnellaktionen',
    recentActivity: 'Letzte Aktivitäten',
    viewAll: 'Alle anzeigen',
    viewDetails: 'Details anzeigen',
    showLess: 'Weniger anzeigen',
    investor: 'Investor',
    premiumMember: 'Premium Mitglied',
    notifications: 'Benachrichtigungen',
    markAllRead: 'Alle als gelesen markieren',
    // ==================== PORTFOLIO VIEW ====================
    portfolioSummary: 'Portfolio Zusammenfassung',
    yourInvestmentOverview: 'Ihre Investment-Übersicht',
    purchaseValueLabel: 'KAUFWERT',
    currentValueLabel: 'AKTUELLER WERT',
    totalGainsLabel: 'GESAMTGEWINN',
    yourProperties: 'Ihre Immobilien',
    xProperties: 'Immobilien',
    vacant: 'Leer',
    occupied: 'Vermietet',
    offplan: 'Off-Plan',
    construction: 'Im Bau',
    toBeRentedOut: 'Zu vermieten',
    undergoingUpgrade: 'In Renovierung',
    // ==================== PROPERTIES ====================
    myProperties: 'Meine Immobilien',
    propertyDetails: 'Immobiliendetails',
    unitNumber: 'Einheitsnummer',
    unit: 'Einheit',
    project: 'Projekt',
    projectName: 'Projektname',
    developer: 'Entwickler',
    location: 'Standort',
    area: 'Fläche',
    sqft: 'sqft',
    bedrooms: 'Schlafzimmer',
    bathrooms: 'Badezimmer',
    propertyType: 'Immobilientyp',
    purchasePrice: 'Kaufpreis',
    purchaseValue: 'Kaufwert',
    purchaseDate: 'Kaufdatum',
    completionDate: 'Fertigstellungsdatum',
    noPropertiesYet: 'Noch keine Immobilien',
    noPropertiesDesc: 'Ihre Immobilien erscheinen hier, sobald sie hinzugefügt wurden.',
    noPropertiesFound: 'Keine Immobilien gefunden',
    addPropertiesToCalculate: 'Fügen Sie Immobilien zu Ihrem Portfolio hinzu, um den ROI zu berechnen.',
    addProperty: 'Immobilie hinzufügen',
    createNewProperty: 'Neuen Immobilienordner erstellen',
    createProperty: 'Immobilie erstellen',
    creating: 'Wird erstellt...',
    propertyName: 'Immobilienname',
    enterPropertyName: 'Immobilienname eingeben',
    pleaseEnterPropertyName: 'Bitte geben Sie einen Immobiliennamen ein',
    errorCreatingProperty: 'Fehler beim Erstellen der Immobilie. Bitte versuchen Sie es erneut.',
    browseProperties: 'Immobilien durchsuchen',
    exploreProperties: 'Immobilien erkunden',
    apartment: 'Wohnung',
    villa: 'Villa',
    townhouse: 'Reihenhaus',
    penthouse: 'Penthouse',
    studio: 'Studio',
    bedroom: 'Schlafzimmer',
    floor: 'Etage',
    projectStatus: 'Projektstatus',
    projectProgress: 'Projektfortschritt',
    completed: 'Abgeschlossen',
    inProgress: 'In Bearbeitung',
    priceAndValuation: 'Preis & Bewertung',
    gains: 'Gewinn (%)',
    summary: 'Zusammenfassung',
    income: 'Einkommen',
    incomeDetails: 'Einkommensdetails',
    monthlyRental: 'Monatliche Miete',
    annualIncome: 'Jahreseinkommen',
    searchPlaceholder: 'Nach Einheit, Dateiname, etc. suchen...',
    requestDocuments: 'Dokumente anfordern',
    showingAll: 'Zeige alle',
    searchProperties: 'Immobilien suchen...',
searchUnits: 'Einheiten suchen...',
requestAddUnit: 'Einheit nicht gefunden? Anfrage stellen',
unitRequestSent: 'Anfrage gesendet! Unser Team wird die Einheit in Kürze hinzufügen.',
tapToUpload: 'Zum Hochladen tippen',
selectUnitFirst: 'Bitte zuerst eine Einheit auswählen',
aiExtraction: 'KI-Extraktion',
aiExtractionDesc: 'Immobiliendaten werden automatisch aus diesem Dokument extrahiert',
processingAI: 'KI-Verarbeitung - Immobiliendaten werden extrahiert...',
uploadingDoc: 'Dokument wird hochgeladen...',
iHaveDocument: 'Ich habe ein Dokument',
enterManually: 'Details manuell eingeben',
projectPlaceholder: 'z.B. Aeon Tower, Ocean Star, Clearpoint',
unitPlaceholder: 'z.B. Gebäude 1, Einheit 905 oder Villa 23',
docUploadNote: 'Wir erstellen die Einheit und kontaktieren Sie zum Hochladen des Dokuments.',
enterProjectUnit: 'Bitte Projektname und Einheitsnummer eingeben',
    // ==================== DOCUMENTS ====================
    documents: 'Dokumente',
    uploadDocument: 'Dokument hochladen',
    noDocuments: 'Noch keine Dokumente',
    noDocumentsUploaded: 'Noch keine Dokumente hochgeladen',
    viewDocument: 'Dokument ansehen',
    addDocuments: 'Dokumente hinzufügen',
    uploadToProperty: 'Dokumente zu Ihrer Immobilie hochladen',
    emptyFolderNote: 'Bitte laden Sie ein Dokument hoch, um die Daten Ihrer Immobilie zu aktualisieren',
    emptyFolderUploadBtn: 'Dokument jetzt hochladen',
    verificationPending: 'Überprüfung ausstehend',
    verificationVerified: 'Verifiziert',
    verificationRejected: 'Daten abgelehnt — Bitte erneut hochladen',
    verificationInProgress: 'Unser Team überprüft die KI-extrahierten Daten',
    verificationComplete: 'Dokumentdaten wurden verifiziert und angewendet',
    verificationRejectedNote: 'Die extrahierten Daten konnten nicht verifiziert werden. Bitte laden Sie ein klareres Dokument hoch.',
    dataExtractedByAI: 'Daten von KI extrahiert',
    awaitingAdminReview: 'Warten auf Admin-Überprüfung vor Aktualisierung der Immobiliendaten',
    documentCategory: 'Dokumentkategorie',
    unitDocuments: 'Einheitsdokumente',
    personalDocuments: 'Persönliche Dokumente',
    selectUnit: 'Einheit auswählen',
    selectAUnit: 'Einheit auswählen...',
    documentType: 'Dokumenttyp',
    chooseFile: 'Datei auswählen',
    uploading: 'Wird hochgeladen...',
    passport: 'Reisepass',
    emiratesId: 'Emirates ID',
    visa: 'Visum',
    paymentProof: 'Zahlungsnachweis',
    floorPlan: 'Grundriss',
    showing: 'Zeige',
    all: 'Alle',
    // ==================== PAYMENT SCHEDULE / CASHFLOW ====================
    paymentSchedule: 'Zahlungsplan',
    installmentPlan: 'Ratenzahlungsplan',
    trackPaymentMilestones: 'Verfolgen Sie Ihre Investment-Zahlungsmeilensteine',
    noPaymentSchedule: 'Kein Zahlungsplan gefunden.',
    uploadSpaForPayments: 'Laden Sie ein SPA-Dokument hoch, um Zahlungsdetails zu sehen',
    upcomingPayments: 'Anstehende Zahlungen',
    paymentAmount: 'Zahlungsbetrag',
    dueDate: 'Fälligkeitsdatum',
    paymentProgress: 'Zahlungsfortschritt',
    allProperties: 'Alle Immobilien',
    paidLabel: 'Bezahlt',
    upcomingLabel: 'Anstehend',
    tpChequeSubmitted: 'Beim Eigentümer',
    overdueLabel: 'Überfällig',
    remainingLabel: 'Ausstehend',
    perUnit: 'Pro Einheit',
    yearlySummary: 'Jahresübersicht',
    yearlyPayments: 'Jährliche Zahlungen',
    noInstallmentData: 'Keine Ratenzahlungsdaten verfügbar',
    uploadSpaOtp: 'Laden Sie SPA, OTP oder SOA hoch, um Ihren Zahlungsplan zu sehen',
    extractFromDocs: 'Aus vorhandenen Dokumenten extrahieren',
    monthlyTotal: 'Monatssumme',
    // ==================== CASHFLOW COMING SOON ====================
    cashflowComingSoon: 'Cashflow-Verfolgung',
    comingSoon: 'Demnächst verfügbar',
    cashflowComingSoonDescription: 'Wir arbeiten an einem fortschrittlichen Cashflow-Tracking-System, um Ihnen bei der Überwachung Ihrer Zahlungspläne, Meilensteine und Immobilieninvestitionen zu helfen.',
    cashflowFeature: 'Cashflow-Verfolgung',
    cashflowComingSoonDesc: 'Wir entwickeln ein leistungsstarkes Cashflow-Management-System, um Zahlungen, Meilensteine und Investitionsfortschritte zu verfolgen.',
    upcomingFeatures: 'Was kommt',
    paymentTracking: 'Zahlungsverfolgung',
    paymentTrackingDesc: 'Verfolgen Sie alle Ihre Immobilienzahlungen mit Statusaktualisierungen und Fälligkeitserinnerungen.',
    milestoneAlerts: 'Meilenstein-Benachrichtigungen',
    milestoneAlertsDesc: 'Erhalten Sie Benachrichtigungen über bevorstehende Zahlungsmeilensteine und Baufortschritte.',
    progressReports: 'Fortschrittsberichte',
    progressReportsDesc: 'Visuelle Diagramme und Berichte, die Ihren Investment-Cashflow über die Zeit zeigen.',
    stayTuned: 'Bleiben Sie dran! Diese Funktion wird bald verfügbar sein.',
    // ==================== INCOME COMING SOON ====================
    incomeTracking: 'Einkommensverfolgung',
    incomeComingSoonDesc: 'Wir entwickeln eine umfassende Einkommensverfolgung, um Ihnen bei der Überwachung von Mieteinnahmen, ROI und finanzieller Performance zu helfen.',
    roiAnalysis: 'ROI-Analyse',
    expenseTracking: 'Ausgabenverfolgung',
    featureInDevelopment: 'Funktion in Entwicklung',
    // ==================== PROFILE ====================
    profileInformation: 'Profilinformationen',
    managePersonalInfo: 'Verwalten Sie Ihre persönlichen Daten',
    editProfile: 'Profil bearbeiten',
    fullName: 'Vollständiger Name',
    emailAddress: 'E-Mail-Adresse',
    phoneNumber: 'Telefonnummer',
    nationality: 'Nationalität',
    dateOfBirth: 'Geburtsdatum',
    accountId: 'Konto-ID',
    changePassword: 'Passwort ändern',
    updateAccountPassword: 'Aktualisieren Sie Ihr Kontopasswort',
    currentPassword: 'Aktuelles Passwort',
    newPassword: 'Neues Passwort',
    confirmPassword: 'Passwort bestätigen',
    enterCurrentPassword: 'Aktuelles Passwort eingeben',
    enterNewPassword: 'Neues Passwort eingeben',
    // ==================== ROI CALCULATOR ====================
    roiCalculatorTitle: 'Investment ROI Rechner',
    investmentReturnAnalysis: 'Renditeanalyse',
    investmentAnalysis: 'Investment Analyse',
    appreciation: 'Wertsteigerung',
    units: 'Einheiten',
    allUnits: 'Alle Einheiten',
    paidPercent: 'Bezahlt %',
    fullyPaid: 'Vollständig bezahlt',
    sellingPrice: 'Verkaufspreis',
    paidInstallment: 'Bezahlte Rate',
    grossNet: 'Brutto Netto',
    profit: 'Gewinn',
    unitBreakdown: 'Einheiten Aufschlüsselung',
    originalPrice: 'Ursprünglicher Preis',
    originalValue: 'Ursprünglicher Wert',
    paid: 'Bezahlt',
    paidAmount: 'Bezahlter Betrag',
    amountPaid: 'Bezahlter Betrag',
    netToSeller: 'Netto an Verkäufer',
    dldFee: 'DLD Gebühr',
    brokerFee: 'Maklergebühr',
    brokerCommission: 'Maklerprovision',
    totalOriginal: 'Gesamt Original',
    totalSelling: 'Gesamt Verkauf',
    totalPaid: 'Gesamt Bezahlt',
    totalFees: 'Gesamt Gebühren',
    totalGrossNet: 'Gesamt Brutto Netto',
    remainingBalance: 'Restbetrag',
    totalProfit: 'Gesamtgewinn',
    profitDividedByInvestment: 'Gewinn ÷ Gesamtinvestition',
    profitDividedByEquity: 'Gewinn ÷ Eigenkapital',
    howRoiCalculated: 'Wie ROI & ROE berechnet werden',
    understandingRoiCalculator: 'ROI Rechner verstehen',
    whatIsRoi: 'Was ist ROI (Return on Investment)?',
    whatIsRoe: 'Was ist ROE (Return on Equity)?',
    whyRoeHigher: 'Warum ist ROE höher als ROI?',
    whatFeesIncluded: 'Welche Gebühren sind enthalten?',
    howProfitCalculated: 'Wie wird der Gewinn berechnet?',
    roiExplanation: 'ROI misst den Gewinn im Verhältnis zu den Gesamtkosten der Immobilie. Es zeigt Ihren Gewinn als Prozentsatz des gesamten Investitionswerts.',
    roeExplanation: 'ROE misst den Gewinn im Verhältnis zu Ihrem tatsächlich investierten Kapital (Eigenkapital). Bei Off-Plan ist dies der bisher bezahlte Betrag.',
    roeHigherExplanation: 'ROE ist höher, weil Sie die Rendite nur gegen das von Ihnen investierte Geld messen, nicht gegen den vollen Immobilienwert. Das ist die Kraft der Hebelwirkung bei Off-Plan-Immobilien.',
    // ==================== ROI CALCULATOR - CORPORATE STRUCTURING ====================
    corporateStructuring: 'Unternehmensstrukturierung',
    structuringRecommendation: 'Strukturierungsempfehlung',
    yourProjectedProfit: 'Ihr prognostizierter Gewinn',
    structuringNotNeeded: 'Strukturierung nicht notwendig',
    structuringNotNeededDesc: 'Bei diesem Gewinnniveau übersteigen die Kosten einer Unternehmensstruktur in der Regel den Nutzen.',
    considerStructuring: 'Strukturierung in Betracht ziehen',
    considerStructuringDesc: 'Ihr Gewinnniveau erreicht den Bereich, in dem eine Unternehmensstruktur steuerliche Vorteile bieten könnte.',
    structuringRecommended: 'Strukturierung empfohlen',
    structuringRecommendedDesc: 'Bei diesem Gewinnniveau kann eine professionelle Unternehmensstruktur Ihre Steuersituation erheblich optimieren.',
    structuringHighlyRecommended: 'Strukturierung dringend empfohlen',
    structuringHighlyRecommendedDesc: 'Bei Gewinnen auf diesem Niveau ist eine professionelle Steuerstrukturierung für optimalen Vermögenserhalt unerlässlich.',
    structureMakesSenseFrom: 'Eine Unternehmensstruktur lohnt sich in der Regel ab',
    profitOnwards: 'Gewinn aufwärts',
    basedOnYourPortfolio: 'Basierend auf Ihrer Portfolio-Analyse',
    bookTaxConsultation: 'Steuerberatung buchen',
    consultationDesc: 'Besprechen Sie Unternehmensstrukturierung, Steueroptimierung & internationale Planung',
    freeConsultation: 'Kostenloses Erstgespräch',
    withTaxExpert: 'mit unseren internationalen Steuerexperten',
    potentialSavings: 'Potenzielle Steuerersparnis',
    savingsDisclaimer: 'Die geschätzte Ersparnis hängt von individuellen Umständen und Heimatland-Steuersätzen ab.',
    thresholdReached: 'Schwellenwert erreicht',
    belowThreshold: 'Unter Schwellenwert',
    // ==================== CORPORATE SERVICES UAE ====================
    corporateServicesUae: 'Unternehmensservices UAE',
    companyManagement: 'Unternehmensverwaltung Dashboard',
    companyFormation: 'Firmengründung Emirates',
    documentHub: 'Dokumentenzentrale',
    tradeLicense: 'Gewerbeschein',
    moa: 'Gesellschaftsvertrag (MOA)',
    unitLeaseAgreement: 'Mietvertrag Geschäftseinheit',
    establishmentCard: 'Establishment Card',
    bankAccountOpening: 'Bankkontoeröffnung',
    visaSetup: 'Visum Einrichtung',
    deadlinesCompliance: 'Fristen & Compliance',
    ctAndFilings: 'Körperschaftssteuer & Meldungen',
    accounting: 'Buchhaltung',
    receiptsAndExpenses: 'Belege & Ausgaben',
    licenseManagement: 'Lizenzverwaltung',
    renewalAndCompliance: 'Verlängerung & Compliance',
    vatManagement: 'Umsatzsteuer-Verwaltung',
    valueAddedTax: 'Mehrwertsteuer (VAT)',
    licenseNo: 'Lizenz-Nr',
    formationProgress: 'Gründungsfortschritt',
    licenseExpiry: 'Lizenzablauf',
    vatStatus: 'USt-Status',
    ctStatus: 'KSt-Status',
    days: 'Tage',
    daysLeft: 'Tage übrig',
    daysRemaining: 'Tage verbleibend',
    registered: 'Registriert',
    notRequired: 'Nicht erforderlich',
    actionRequired: 'Handlung erforderlich',
    compliant: 'Konform',
    actionNeeded: 'Handlung nötig',
    required: 'Erforderlich',
    optional: 'Optional',
    verified: 'Verifiziert',
    pending: 'Ausstehend',
    upload: 'Hochladen',
    overallProgress: 'Gesamtfortschritt',
    ctRegistrationRequired: 'KSt-Registrierung erforderlich',
    ctRegistrationDeadline: 'Registrierungsfrist',
    registerNow: 'Jetzt registrieren',
    ctFilingDue: 'KSt-Abgabe fällig',
    nextFilingDeadline: 'Nächste Abgabefrist',
    bookAppointment: 'Termin buchen',
    submitFiling: 'Meldung einreichen',
    uploadReceipt: 'Beleg hochladen',
    receipts: 'Belege',
    licenseExpiryWarning: 'Lizenz läuft bald ab!',
    licenseExpiresOn: 'Lizenz läuft ab am',
    renewLicense: 'Lizenz verlängern',
    licenseValid: 'Lizenz gültig',
    expiresOn: 'Läuft ab am',
    pushNotificationInfo: 'Push-Benachrichtigung aktiviert',
    licenseReminderNote: 'Sie erhalten 2 Monate vor Ablauf Ihrer Lizenz eine Erinnerung.',
    revenueProgress: 'Umsatz vs. USt-Schwellenwert',
    vatThresholdNote: 'Die USt-Registrierung ist bei Umsätzen über 375.000 AED Pflicht',
    vatRegistrationRequired: 'USt-Registrierung erforderlich',
    vatThresholdExceeded: 'Ihr Umsatz hat den USt-Schwellenwert überschritten. Die Registrierung ist Pflicht.',
    registerForVat: 'Jetzt für USt registrieren',
    vatNotRequired: 'USt nicht erforderlich',
    vatBelowThreshold: 'Ihr Umsatz liegt unter dem USt-Schwellenwert',
    remaining: 'verbleibend',
    registrationRequired: 'Registrierung erforderlich',
    vatDocuments: 'USt-Dokumente',
    uploadVatReceipt: 'USt-Beleg hochladen',
    uploadVatFiling: 'USt-Meldung hochladen',
    needHelp: 'Brauchen Sie Hilfe mit Ihrem Unternehmen?',
    clickToUpload: 'Klicken oder ziehen zum Hochladen',
    // NEW: Corporate Services - Company Setup & Management
    setupYourCompany: 'Richten Sie Ihr UAE-Unternehmen ein',
    companySetup: 'Unternehmenseinrichtung',
    companyName: 'Firmenname',
    licenseNumber: 'Lizenznummer',
    freeZone: 'Freizone',
    createCompany: 'Unternehmen erstellen',
    companySetupNote: 'Sie können diese Details später in den Einstellungen ändern.',
    expired: 'Abgelaufen',
    checkWithAdvisor: 'Mit Berater klären',
    toBeConfirmed: 'Wird noch bestätigt',
    confirmDeleteDocument: 'Sind Sie sicher, dass Sie dieses Dokument löschen möchten?',
    confirmDeleteReceipt: 'Sind Sie sicher, dass Sie diesen Beleg löschen möchten?',
    pleaseEnterCompanyDetails: 'Bitte geben Sie Firmennamen und Lizenznummer ein',
    pleaseEnterReceiptDetails: 'Bitte geben Sie Beschreibung und Betrag ein',
    description: 'Beschreibung',
    date: 'Datum',
    addReceipt: 'Beleg hinzufügen',
    noReceiptsYet: 'Noch keine Belege hochgeladen',
    viewReceipts: 'Belege anzeigen',
    totalVatPaid: 'Gesamt USt bezahlt',
    amountIncludesVat: 'Betrag enthält USt (5%)',
    // VAT Self-Calculator
    vatSelfCalculator: 'USt-Selbstrechner',
    currentRevenue: 'Aktueller Umsatz',
    vatThreshold: 'USt-Schwellenwert',
    amountOverThreshold: 'Betrag über Schwellenwert',
    estimatedVatOwed: 'Geschätzte USt-Schuld',
    vatCalculatorNote: 'Dies ist eine Schätzung. Konsultieren Sie einen Steuerberater für genaue Berechnungen.',
    // Formation Timeline
    nextStep: 'Nächster Schritt',
    step: 'Schritt',
    current: 'Aktuell',
    uploadNow: 'Jetzt hochladen',
    // Apple Pay
    applePayComingSoon: 'Apple Pay Integration kommt bald! Bitte buchen Sie vorerst einen Termin zur Zahlungsabwicklung.',
    // License Expired
    licenseExpired: 'Lizenz abgelaufen!',
    // ==================== CASHFLOW CALCULATOR ====================
    cashflowCalculator: 'Cashflow Rechner',
    grossNetAnalysis: 'Brutto-Netto Analyse für Immobilieninvestments',
    property: 'Immobilie',
    year: 'Jahr',
    totalIncome: 'Gesamteinkommen',
    totalExpenses: 'Gesamtausgaben',
    netCashflow: 'Netto Cashflow',
    totalRoi: 'Gesamt ROI',
    annual: 'Jährlich',
    includingAppreciation: 'Inkl. Wertsteigerung',
    propertyAppreciation: 'Immobilien Wertsteigerung',
    expenses: 'Ausgaben',
    addExpense: 'Ausgabe hinzufügen',
    addIncome: 'Einkommen hinzufügen',
    category: 'Kategorie',
    amount: 'Betrag',
    enterAmount: 'Betrag eingeben',
    frequency: 'Häufigkeit',
    oneTime: 'Einmalig',
    monthly: 'Monatlich',
    quarterly: 'Quartalsweise',
    incomeType: 'Einkommensart',
    pleaseSelectProperty: 'Bitte wählen Sie zuerst eine Immobilie',
    pleaseEnterAmount: 'Bitte geben Sie einen Betrag ein',
    notApplied: 'Nicht angewendet',
    serviceCharges: 'Servicegebühren',
    dewa: 'DEWA (Strom/Wasser)',
    propertyManagement: 'Immobilienverwaltung',
    maintenance: 'Wartung & Reparaturen',
    insurance: 'Versicherung',
    mortgagePayment: 'Hypothekenzahlung',
    furnishing: 'Einrichtung',
    agencyFees: 'Agenturgebühren',
    legalFees: 'Rechtskosten',
    other: 'Sonstiges',
    rentalIncome: 'Mieteinnahmen',
    airbnbIncome: 'Airbnb Einnahmen',
    expectedAppreciation: 'Erwartete Wertsteigerung',
    otherIncome: 'Sonstige Einnahmen',
    // ==================== SUPPORT ====================
    supportTitle: 'Support',
    howCanWeHelp: 'Wie können wir Ihnen helfen?',
    yourAccountManager: 'Ihr Kundenbetreuer',
    yourPersonalAdvisor: 'Ihr persönlicher Berater',
    seniorInvestmentAdvisor: 'Senior Investmentberater',
    noAssignedAdvisor: 'Kein zugewiesener Berater',
    noAssignedSupport: 'Kein zugewiesener Support',
    messageOnWhatsApp: 'Nachricht auf WhatsApp',
    connecting: 'Verbindung wird hergestellt...',
    callUs: 'Anrufen',
    email: 'E-Mail',
    emailUs: 'E-Mail senden',
    whatsapp: 'WhatsApp',
    liveChat: 'Live-Chat',
    otherWaysToReach: 'Weitere Kontaktmöglichkeiten',
    getInstantAnswers: 'Erhalten Sie sofortige Antworten auf Ihre Fragen',
    needToTalkHuman: 'Möchten Sie mit einem Mitarbeiter sprechen?',
    aiAssistantTitle: 'Asset Desk KI Assistent',
    welcomeToAI: 'Willkommen beim Asset Desk KI',
    aiWelcomeMessage: 'Ich bin Ihr persönlicher Investmentassistent. Fragen Sie mich alles über Dubai Immobilien, Ihr Portfolio oder Investitionsstrategien.',
    typeMessage: 'Nachricht eingeben...',
    // ==================== SECURITY ====================
    twoFaDisabledDesc: '⚠ 2FA ist deaktiviert. Aktivieren Sie es, um eine zusätzliche Sicherheitsebene hinzuzufügen.',
    twoFaEnabledDesc: '✓ 2FA ist aktiviert. Ihr Konto hat eine zusätzliche Sicherheitsebene.',
    enableTwoFactorAuth: 'Zwei-Faktor-Authentifizierung aktivieren',
    faceRecognitionDisabledDesc: '⚠ Gesichtserkennung ist deaktiviert. Aktivieren Sie sie für schnelles und sicheres Anmelden.',
    faceRecognitionEnabledDesc: '✓ Gesichtserkennung ist aktiviert. Sie können sich mit Ihrem Gesicht anmelden.',
    enableFaceRecognition: 'Gesichtserkennung aktivieren',
    disabled: 'DEAKTIVIERT',
    enabled: 'AKTIVIERT',
    settingUp: 'Wird eingerichtet...',
    // ==================== MAIN DASHBOARD ====================
    investInDubai: 'In Dubai Immobilien investieren',
    discoverExclusiveProperties: 'Entdecken Sie exklusive Off-Plan-Immobilien mit garantierten Renditen',
    dubaiRealEstateNews: 'Dubai Immobilien Nachrichten',
    latestNewsSubtitle: 'Neueste Nachrichten und Updates vom Immobilienmarkt Dubai',
    documentRequest: 'Dokumentenanfrage',
    serviceRequest: 'Serviceanfrage',
    pointOfContact: 'Ansprechpartner',
    bookConsultant: 'Berater buchen',
    requests: 'Anfragen',
    paymentDue: 'Zahlung fällig',
    nextPaymentIn7Days: 'Nächste Zahlung in 7 Tagen',
    constructionUpdate: 'Baufortschritt Update',
    newDocument: 'Neues Dokument',
    uploaded: 'hochgeladen',
    newProperties: 'Neue Immobilien',
    checkLatestOfferings: 'Entdecken Sie unsere neuesten Angebote',
    viewBrochure: 'Broschüre ansehen',
    watchVideo: 'Video ansehen',
    upcomingProjects: 'Kommende Projekte',
    exclusiveLaunches: 'Exklusive Launches in Kürze',
    // ==================== PROPERTY VIEW ====================
    paymentDetails: 'Zahlungsdetails',
    milestone: 'Meilenstein',
    status: 'Status',
    valuation: 'Bewertung',
    overduePaymentsAlert: 'Überfällige Zahlungen Warnung',
    pendingVerificationNote: 'Dokumente warten auf Verifizierung',
    uploadDocsNote: 'Dokumente hochladen, um Ihre Immobiliendaten zu aktualisieren',
personalDocs: 'Persönlich',
uploadPersonalDoc: 'Persönliches Dokument hochladen',
noPersonalDocs: 'Noch keine persönlichen Dokumente',
uploadPassportVisa: 'Laden Sie Ihren Reisepass, Visum oder Emirates ID hoch',
passportNo: 'Reisepass-Nr',
visaNo: 'Visum-Nr',
visaType: 'Visumart',
idNo: 'ID-Nr',
expiry: 'Gültig bis',
companyDocs: 'Unternehmen',
uploadCompanyDoc: 'Unternehmensdokument hochladen',
noCompanyDocs: 'Noch keine Unternehmensdokumente',
uploadCompanyDocsDesc: 'Laden Sie Ihre Gewerbegenehmigung, Vollmacht, MOA und mehr hoch',
companyDocuments: 'Unternehmensdokumente',
    // ==================== SECURITY SETTINGS ====================
    securitySettings: 'Sicherheitseinstellungen',
    twoFactorAuth: 'Zwei-Faktor-Authentifizierung (2FA)',
    facialRecognitionLogin: 'Gesichtserkennung Login',
    faceRecognition: 'Gesichtserkennung',
    enableFacialRecognition: 'Gesichtserkennung aktivieren',
    disableFaceRecognition: 'Gesichtserkennung deaktivieren',
    viewBackupCodes: 'Backup-Codes anzeigen',
    scanQrCode: 'Scannen Sie diesen QR-Code mit Ihrer Authenticator-App (Google Authenticator, Authy, etc.)',
    cantScanEnterManually: 'Scannen nicht möglich? Code manuell eingeben:',
    step2SaveBackupCodes: 'Schritt 2: Backup-Codes speichern',
    copyBackupCodes: 'Backup-Codes kopieren',
    step3VerifySetup: 'Schritt 3: Einrichtung überprüfen',
    enterCodeFromAuthenticator: 'Code aus Ihrer Authenticator-App eingeben',
    saveBackupCodesWarning: 'Speichern Sie diese Backup-Codes an einem sicheren Ort. Sie können diese verwenden, um auf Ihr Konto zuzugreifen, falls Sie Ihr Telefon verlieren.',
    copyCodes: 'Codes kopieren',
    positionFaceInstruction: 'Positionieren Sie Ihr Gesicht in der Mitte der Kamera. Achten Sie auf gute Beleuchtung.',
    faceDataEncrypted: 'Ihre Gesichtsdaten sind verschlüsselt und sicher gespeichert',
    // ==================== BIOMETRIC AUTH ====================
    biometricLogin: 'Biometrische Anmeldung',
    enableBiometricLogin: 'Biometrische Anmeldung aktivieren',
    disableBiometricLogin: 'Biometrische Anmeldung deaktivieren',
    biometricLoginEnabled: 'Biometrische Anmeldung erfolgreich aktiviert!',
    biometricLoginDisabled: 'Biometrische Anmeldung wurde deaktiviert.',
    biometricNotAvailable: 'Biometrische Authentifizierung ist auf diesem Gerät nicht verfügbar',
    biometricNotSetUp: 'Face ID ist noch nicht eingerichtet. Melden Sie sich einmal mit Ihrem Passwort an, dann bietet die App es Ihnen an.',
    faceIdOffer: 'Beim nächsten Mal mit Face ID anmelden?\n\nBeim nächsten Öffnen der App genügt ein Blick auf Ihr Telefon. Sie können es jederzeit in Ihrem Profil ausschalten.',
    enterPasswordToEnable: 'Geben Sie Ihr Passwort ein, um die biometrische Anmeldung zu aktivieren:',
    incorrectPassword: 'Falsches Passwort. Bitte versuchen Sie es erneut.',
    authenticationCancelled: 'Authentifizierung wurde abgebrochen',
    authenticationFailed: 'Biometrische Authentifizierung fehlgeschlagen',
    passwordMayHaveChanged: 'Anmeldung fehlgeschlagen. Ihr Passwort könnte geändert worden sein. Bitte melden Sie sich mit Passwort an.',
    loginWithBiometric: 'Mit Face ID / Fingerabdruck anmelden',
    useFaceOrFingerprint: 'Verwenden Sie Ihr Gesicht oder Ihren Fingerabdruck zur Anmeldung',
    accountNotFound: 'Konto nicht gefunden',
    // ==================== MARKETPLACE/PROPERTIES ====================
    availableProperties: 'Verfügbare Immobilien',
    browseCompletePortfolio: 'Entdecken Sie unser komplettes Portfolio an Dubai-Immobilien für Investitionen',
    bookAnAppointment: 'Termin buchen',
    joinMeeting: 'Meeting beitreten',
    requestMeetingLink: 'Meeting-Link anfordern',
    tapToInquireWhatsApp: 'Tippen Sie hier, um per WhatsApp anzufragen',
    tapToInquireChat: 'Tippen Sie hier, um per Chat anzufragen',
    // ==================== MENU/PROFILE SECTION ====================
    totalProperties: 'Immobilien gesamt',
    accountAge: 'Kontoalter',
    yourInvestmentTeam: 'Ihr Investment Team',
    dedicatedContacts: 'Ihre persönlichen Ansprechpartner bei Asset Desk',
    investmentAdvisor: 'Investment Berater',
    supportContact: 'Support Kontakt',
    totalCapitalGain: 'Gesamtkapitalgewinn',
    capitalGain: 'Kapitalgewinn',
    trackInstallments: 'Verfolgen Sie Ihre Ratenzahlungen',
    followStepsToSchedule: 'Folgen Sie den Schritten unten, um Ihren Termin zu planen.',
    selectServiceType: '1. Serviceart wählen',
    corporateServices: 'Unternehmensdienstleistungen',
    conveyanceServices: 'Übertragungsdienstleistungen',
    selectService: '2. Service wählen',
    chooseService: '-- Service wählen --',
    selectAvailableTime: '4. Verfügbare Zeit wählen',
    // ==================== COMMON ====================
    loading: 'Laden...',
    loadingNews: 'Nachrichten werden geladen...',
    save: 'Speichern',
    cancel: 'Abbrechen',
    confirm: 'Bestätigen',
    delete: 'Löschen',
    edit: 'Bearbeiten',
    close: 'Schließen',
    submit: 'Absenden',
    search: 'Suchen',
    filter: 'Filter',
    error: 'Fehler',
    success: 'Erfolg',
    aed: 'AED',
    bookConsultation: 'Beratung buchen',
    yourBookedAppointments: 'Ihre gebuchten Termine',
    footerText: '© 2026 Asset Desk Real Estate LLC. Premium-Verwaltung für Ihr Immobilienportfolio.',
    smartUpload: 'Smart Upload',
    smartUploadDesc: 'Laden Sie alle Dateien auf einmal hoch - KI sortiert alles automatisch',
    smartUploadBtn: 'Alle Dokumente hochladen (KI Smart Upload)',
    smartUploadHint: 'Alle Dateien auf einmal auswaehlen - KI sortiert & prueft automatisch',
    tapToUploadAll: 'Tippen Sie hier um alle Dokumente hochzuladen',
    smartUploadFormats: 'Mehrere Dateien auswaehlen - PDF, JPG, PNG (Max 100MB)',
    smartUploadVerify: 'Alle Dokumente werden von unserem Team geprueft bevor sie in Ihrem Konto erscheinen',
    orUploadManually: 'ODER MANUELL HOCHLADEN',
    emailDocuments: 'Dokumente per E-Mail',
    emailDocumentsDesc: 'Senden Sie alle Dokumente an unsere E-Mail - wir erledigen den Rest',
    emailDocumentsBtn: 'Dokumente per E-Mail senden',
    // ==================== BEWERTUNGSBERICHT ====================
    vrTitle: 'Bewertungsbericht',
    vrGenerated: 'Erstellt',
    vrDldSource: 'Daten des Dubai Land Department',
    vrAllProperties: 'Alle Immobilien',
    vrDownloadPdf: 'PDF-Bericht herunterladen',
    vrPreparing: 'Bericht wird erstellt…',
    vrProperty: 'Immobilie',
    vrUnitNumber: 'Einheitennummer',
    vrType: 'Typ',
    vrBedrooms: 'Schlafzimmer',
    vrBuiltUpArea: 'Wohnfläche',
    vrStatus: 'Status',
    vrPurchased: 'Gekauft am',
    vrPurchasePrice: 'Kaufpreis',
    vrHandover: 'Übergabe',
    vrDisclaimer: 'Haftungsausschluss',
    vrDldMarketValue: 'DLD-Marktwert',
    vrEstimateRange: 'Schätzspanne',
    vrPricePerSqft: 'Preis pro sqft',
    vrComparablesUsed: 'Verwendete Vergleichsobjekte',
    vrLast6Months: 'Veränderung (6 Monate)',
    vrIndicativeOnly: 'Nur Richtwert',
    vrNotEnoughComps: 'Noch nicht genügend vergleichbare Verkäufe für eine Bewertung dieser Einheit',
    vrRangeOnly: 'Nur Spanne.',
    vrHowCalculated: 'So wird dieser Wert berechnet',
    vrHowStep1a: 'Wir verwenden die',
    vrHowStep1b: 'beim DLD registrierten Verkäufe vergleichbarer Einheiten (unten aufgeführt) — echte Transaktionen, keine Angebotspreise.',
    vrHowStep2a: 'Jeder Verkauf wird in einen Preis pro sqft umgerechnet. Wir lassen das günstigste und das teuerste Zehntel weg und mitteln den Rest — der',
    vrHowStep2b: 'getrimmte Durchschnitt',
    vrHowStep2c: 'beträgt',
    vrHowStep2d: 'pro sqft. Durch das Trimmen fallen der Schnäppchenverkauf und das Premium-Penthouse heraus, sodass beide den Wert nicht verzerren können, während der Rest des Gebäudes weiterhin zählt. Ein reiner Median würde ausblenden, wie deutlich darüber die besseren Einheiten gehandelt werden.',
    vrHowStep3a: 'pro sqft × die',
    vrHowStep3b: 'sqft dieser Einheit',
    vrRangeExplain: 'Die Spanne umfasst die mittlere Hälfte dieser Verkäufe — das 25. bis 75. Perzentil.',
    vrCompsForEstimate: 'Für diese Schätzung verwendete Vergleichsobjekte',
    vrCompsForEstimateSub: 'Die exakten beim DLD registrierten Verkäufe hinter dem obigen Wert — Median AED/sqft dieser Transaktionen × Größe dieser Einheit. Nichts anderes beeinflusst die Zahl.',
    vrCompsAreaScope: 'Für dieses Gebäude ist kein passender registrierter Verkauf vorhanden, daher stammen diese Verkäufe von vergleichbaren Einheiten in der Umgebung — aus anderen Gebäuden, nicht aus diesem.',
    vrCompsShowAll: 'Alle Vergleichsobjekte anzeigen',
    vrCompsShowFewer: 'Weniger anzeigen',
    vrOtherSales: 'Weitere aktuelle Verkäufe in diesem Gebäude',
    vrOtherSalesSub: 'Alle Einheitstypen und -größen — nur zur Einordnung; diese beeinflussen die obige Schätzung NICHT.',
    vrColDate: 'Datum',
    vrColProperty: 'Immobilie',
    vrColBeds: 'Zimmer',
    vrColArea: 'Fläche',
    vrColSoldAt: 'Verkauft für',
    vrHighlySimilar: 'SEHR ÄHNLICH',
    vrOffPlan: 'Off-Plan',
    vrVsYours: 'vs. Ihre',
    vrDldRef: 'DLD-Referenz',
    vrNoComps: 'Noch keine vergleichbaren DLD-Verkäufe für diese Einheit gefunden.',
    vrTwoTiersTitle: 'Dieses Gebäude verkauft auf zwei Preisniveaus.',
    vrTwoTiersOf: 'der',
    vrTwoTiersClosedAt: 'Vergleichsverkäufe lagen bei',
    vrTwoTiersForSize: 'für diese Größe',
    vrTwoTiersWhile: 'während',
    vrTwoTiersHigher: 'deutlich höher lagen, bei',
    vrTwoTiersNoFloor: 'typischerweise höhere Etagen, Ausblick oder Ausstattung. DLD-Aufzeichnungen enthalten weder Etage noch Ausblick, daher können wir nicht feststellen, welchem Niveau diese Einheit zuzuordnen ist. Die obige Schätzung ist der Median aller',
    vrTwoTiersEnd: 'Verkäufe; eine Premium-Einheit läge im oberen Bereich.',
    vrCostBreakdown: 'Kostenaufstellung',
    vrCostBreakdownSub: 'Gesamtkosten für den Erwerb dieser Immobilie zum geschätzten Wert.',
    vrTotalCostToBuy: 'Gesamtkosten des Erwerbs',
    vrEstimatedValue: 'Geschätzter Wert',
    vrDldFee: 'DLD-Übertragungsgebühr',
    vrTrustee: 'Registrierungstreuhänder',
    vrAgencyFee: 'Maklergebühr',
    vrAgainstPurchase: 'Im Vergleich zu Ihrem Kauf',
    vrUnrealisedGain: 'Nicht realisierter Gewinn',
    vrTrendCaption: 'Monatlicher Medianpreis vergleichbarer Einheiten in diesem Gebäude',
    vrCurrentEstimate: 'aktuelle Schätzung',
    vrConfidenceHigh: 'Hohe Konfidenz',
    vrConfidenceMedium: 'Mittlere Konfidenz',
    vrConfidenceLow: 'Geringe Konfidenz',
    vrConfidenceNone: 'Unzureichende Daten',
    vrAsAt: 'Stand',
    vrQualifyingComp: 'qualifiziertes Vergleichsobjekt',
    vrQualifyingComps: 'qualifizierte Vergleichsobjekte',
    vrFewerThan3: '— weniger als 3, daher würde eine einzelne Zahl mehr behaupten, als die Datenlage hergibt. Die Transaktionen unten sind diese Datenlage.',
    languageLabel: 'Sprache',
    languageEnglish: 'English',
    languageGerman: 'Deutsch',
    vrMethodology: 'So wurde dieser Wert berechnet',
    vrMethodologyBody1: 'Median-Quadratfußpreis vergleichbarer Verkäufe des Dubai Land Department, angewendet auf die Größe dieser Einheit — der branchenübliche Ansatz. Vergleichsobjekte sind dasselbe Gebäude und dieselbe Zimmeranzahl, innerhalb von ±15 % der Einheitsgröße, aus den letzten',
    vrMethodologyBody2: 'Monaten, ausschließlich echte Weiterverkäufe, statistische Ausreißer entfernt.',
    vrCompsUsedShort: 'Verwendete Vergleiche',
    vrCompsExcluded: 'Ausgeschlossene Vergleiche',
    vrMatchLevel: 'Zuordnungsebene',
    vrAreaMatched: 'Zugeordnetes Gebiet',
    vrSource: 'Quelle',
    vrSynced: 'synchronisiert',
    vrTenancyTitle: 'Aktuelle Mietverträge',
    vrTenancySub: 'Registrierte Ejari-Mietverträge vergleichbarer Einheiten.',
    vrSpreadTitle: 'Abweichung zwischen Quellen —',
    vrSpreadBody: 'Anbieter gewichten Transaktionen und Angebote unterschiedlich; betrachten Sie die Spanne als Antwort, nicht eine einzelne Zahl.',
    vrColStart: 'Beginn',
    vrColRent: 'Jahresmiete',
    igSkipTutorial: 'Anleitung überspringen',
    igTapHere: 'Hier tippen',
    pnhEnable: 'Push-Benachrichtigungen aktivieren',
    pnhEnableSub: 'Erhalten Sie sofortige Updates auf Ihrem Handy',
    pnhEnableBtn: 'Aktivieren',
  }
};

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const LanguageProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [language, setLanguageState] = useState<Language>(() => {
    // Initialize from localStorage synchronously to avoid flash
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('investmenterLanguage') as Language;
      if (saved === 'en' || saved === 'de') {
        return saved;
      }
    }
    return 'en';
  });

  const setLanguage = useCallback((lang: Language) => {
    setAppLanguage(lang);
    setLanguageState(lang);
    localStorage.setItem('investmenterLanguage', lang);
  }, []);
  // Keep the non-React helpers (src/i18n/locale.ts) on the same language.
  setAppLanguage(language);

  // Area dictionaries (src/i18n/*) merged UNDER the base keys, so a per-screen
  // dictionary can never silently override a shared label. Falls back to English,
  // then to the key itself — a missing translation shows the key, never a blank.
  const dict = useMemo(() => ({
    en: { ...areaTranslations.en, ...translations.en },
    de: { ...areaTranslations.de, ...translations.de },
  }), []);

  const t = useCallback((key: string): string => {
    return dict[language][key] || dict['en'][key] || key;
  }, [language, dict]);

  const value = useMemo(() => ({ 
    language, 
    setLanguage, 
    t 
  }), [language, setLanguage, t]);

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export const LanguageSelector: React.FC<{ compact?: boolean }> = ({ compact = false }) => {
  const { language, setLanguage } = useLanguage();

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: compact ? '6px' : '10px' }}>
      <button
        onClick={() => setLanguage('en')}
        style={{
          padding: compact ? '6px 12px' : '8px 16px',
          border: 'none',
          borderRadius: '8px',
          fontSize: compact ? '0.75rem' : '0.85rem',
          fontWeight: 600,
          cursor: 'pointer',
          background: language === 'en' ? '#D4AF37' : '#e5e5e5',
          color: language === 'en' ? '#000' : '#666',
          transition: 'all 0.2s',
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
        }}
      >
        {compact ? 'EN' : 'English'}
      </button>
      <button
        onClick={() => setLanguage('de')}
        style={{
          padding: compact ? '6px 12px' : '8px 16px',
          border: 'none',
          borderRadius: '8px',
          fontSize: compact ? '0.75rem' : '0.85rem',
          fontWeight: 600,
          cursor: 'pointer',
          background: language === 'de' ? '#D4AF37' : '#e5e5e5',
          color: language === 'de' ? '#000' : '#666',
          transition: 'all 0.2s',
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
        }}
      >
        {compact ? 'DE' : 'Deutsch'}
      </button>
    </div>
  );
};

export default LanguageProvider;